import { type MovedPage, movedPage } from '@/app/movedPage';

/**
 * Each page the new navigation has moved, once, sorted by today's route. Links keep pointing at today's URLs, and with
 * the new navigation on, `_authenticated`'s `beforeLoad` reroutes them to the new route (`app/newNavigation.ts`).
 * `shared/routing/routeTree.test.ts` checks that a page's two route files differ only in their ID and
 * `chrome`, and that the layouts above them declare the same access, query params and loading. A page the new
 * navigation draws anew has a route file of its own, and is listed in that test as `redesigned`.
 */
export const movedPages: ReadonlyArray<MovedPage> = [
    movedPage({ from: '/apps/$', to: '/next/apps/$' }),
    movedPage({ from: '/assemblies', to: '/next/assemblies' }),
    movedPage({ from: '/assemblies/$assemblyId', to: '/next/assemblies/$assemblyId' }),
    movedPage({ from: '/assemblies/$assemblyId/dashboard', to: '/next/assemblies/$assemblyId/dashboard' }),
    movedPage({ from: '/assemblies/$assemblyId/details', to: '/next/assemblies/$assemblyId/details' }),
    movedPage({ from: '/assemblies/$assemblyId/edit', to: '/next/assemblies/$assemblyId/edit' }),
    movedPage({ from: '/assemblies/$assemblyId/pcb', to: '/next/assemblies/$assemblyId/pcb' }),
    movedPage({ from: '/assemblies/$assemblyId/pcb/cad-viewer', to: '/next/assemblies/$assemblyId/pcb/cad-viewer' }),
    movedPage({ from: '/assemblies/$assemblyId/pcb/dfm', to: '/next/assemblies/$assemblyId/pcb/dfm' }),
    movedPage({
        from: '/assemblies/$assemblyId/pcb/dfm-analysis',
        to: '/next/assemblies/$assemblyId/pcb/dfm-analysis',
    }),
    movedPage({ from: '/assemblies/$assemblyId/pcb/drills', to: '/next/assemblies/$assemblyId/pcb/drills' }),
    movedPage({ from: '/assemblies/$assemblyId/pcb/files', to: '/next/assemblies/$assemblyId/pcb/files' }),
    movedPage({
        from: '/assemblies/$assemblyId/pcb/panelization',
        to: '/next/assemblies/$assemblyId/pcb/panelization',
    }),
    movedPage({
        from: '/assemblies/$assemblyId/pcb/specification',
        to: '/next/assemblies/$assemblyId/pcb/specification',
    }),
    movedPage({ from: '/assemblies/$assemblyId/pcb/stackup', to: '/next/assemblies/$assemblyId/pcb/stackup' }),
    movedPage({
        from: '/assemblies/$assemblyId/subassemblies/create',
        to: '/next/assemblies/$assemblyId/subassemblies/create',
    }),
    movedPage({ from: '/customers', to: '/next/customers' }),
    movedPage({ from: '/customers/$customerId', to: '/next/customers/$customerId' }),
    movedPage({ from: '/home', to: '/next/home' }),
    movedPage({ from: '/manufacturers', to: '/next/manufacturers' }),
    movedPage({ from: '/manufacturers/$manufacturerId', to: '/next/manufacturers/$manufacturerId' }),
    movedPage({ from: '/manufacturers/line-card', to: '/next/manufacturers/line-card' }),
    movedPage({ from: '/manufacturing/activities', to: '/next/templates/manufacturing/activities' }),
    movedPage({
        from: '/manufacturing/activities/$activityId/duplicate',
        to: '/next/templates/manufacturing/activities/$activityId/duplicate',
    }),
    movedPage({
        from: '/manufacturing/activities/$activityId/edit',
        to: '/next/templates/manufacturing/activities/$activityId/edit',
    }),
    movedPage({ from: '/manufacturing/activities/create', to: '/next/templates/manufacturing/activities/create' }),
    movedPage({
        from: '/manufacturing/apps/$appId/$widgetId',
        to: '/next/templates/manufacturing/apps/$appId/$widgetId',
    }),
    movedPage({ from: '/manufacturing/drivers', to: '/next/templates/manufacturing/drivers' }),
    movedPage({
        from: '/manufacturing/drivers/$driverId/duplicate',
        to: '/next/templates/manufacturing/drivers/$driverId/duplicate',
    }),
    movedPage({
        from: '/manufacturing/drivers/$driverId/edit',
        to: '/next/templates/manufacturing/drivers/$driverId/edit',
    }),
    movedPage({ from: '/manufacturing/drivers/create', to: '/next/templates/manufacturing/drivers/create' }),
    movedPage({ from: '/manufacturing/expenses', to: '/next/templates/manufacturing/expenses' }),
    movedPage({
        from: '/manufacturing/expenses/$expenseId/duplicate',
        to: '/next/templates/manufacturing/expenses/$expenseId/duplicate',
    }),
    movedPage({
        from: '/manufacturing/expenses/$expenseId/edit',
        to: '/next/templates/manufacturing/expenses/$expenseId/edit',
    }),
    movedPage({ from: '/manufacturing/expenses/create', to: '/next/templates/manufacturing/expenses/create' }),
    movedPage({ from: '/manufacturing/resources', to: '/next/templates/manufacturing/resources' }),
    movedPage({
        from: '/manufacturing/resources/$resourceId/duplicate',
        to: '/next/templates/manufacturing/resources/$resourceId/duplicate',
    }),
    movedPage({
        from: '/manufacturing/resources/$resourceId/edit',
        to: '/next/templates/manufacturing/resources/$resourceId/edit',
    }),
    movedPage({ from: '/manufacturing/resources/create', to: '/next/templates/manufacturing/resources/create' }),
    movedPage({
        from: '/manufacturing/templates/{-$templateId}',
        to: '/next/templates/manufacturing/templates/{-$templateId}',
    }),
    movedPage({ from: '/negotiations', to: '/next/negotiations' }),
    movedPage({ from: '/negotiations/$negotiationId', to: '/next/negotiations/$negotiationId' }),
    movedPage({
        from: '/negotiations/$negotiationId/demand-groups/$demandGroupId',
        to: '/next/negotiations/$negotiationId/demand-groups/$demandGroupId',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/demand-groups/$demandGroupId/awarding',
        to: '/next/negotiations/$negotiationId/demand-groups/$demandGroupId/awarding',
    }),
    movedPage({ from: '/negotiations/$negotiationId/overview', to: '/next/negotiations/$negotiationId/overview' }),
    movedPage({
        from: '/negotiations/$negotiationId/quote-requests',
        to: '/next/negotiations/$negotiationId/quote-requests',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/quote-requests/$quoteRequestId',
        to: '/next/negotiations/$negotiationId/quote-requests/$quoteRequestId',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/quote-requests/assign',
        to: '/next/negotiations/$negotiationId/quote-requests/assign',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/quote-requests/attachments',
        to: '/next/negotiations/$negotiationId/quote-requests/attachments',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/quote-requests/delivery-lot-size',
        to: '/next/negotiations/$negotiationId/quote-requests/delivery-lot-size',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/quote-requests/importer',
        to: '/next/negotiations/$negotiationId/quote-requests/importer',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/quote-requests/line-items',
        to: '/next/negotiations/$negotiationId/quote-requests/line-items',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/quote-requests/preview-email',
        to: '/next/negotiations/$negotiationId/quote-requests/preview-email',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/quote-requests/target-price',
        to: '/next/negotiations/$negotiationId/quote-requests/target-price',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/scenarios/$sourcingScenarioId',
        to: '/next/negotiations/$negotiationId/scenarios/$sourcingScenarioId',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/scenarios/$sourcingScenarioId/cost-breakdown',
        to: '/next/negotiations/$negotiationId/scenarios/$sourcingScenarioId/cost-breakdown',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/scenarios/$sourcingScenarioId/cost-breakdown/assembly/$assemblyId/demand/$demandId',
        to: '/next/negotiations/$negotiationId/scenarios/$sourcingScenarioId/cost-breakdown/assembly/$assemblyId/demand/$demandId',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/scenarios/$sourcingScenarioId/line/$sourcingLineItemId',
        to: '/next/negotiations/$negotiationId/scenarios/$sourcingScenarioId/line-items/$sourcingLineItemId',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/scenarios/compare',
        to: '/next/negotiations/$negotiationId/scenarios/compare',
    }),
    movedPage({
        from: '/negotiations/$negotiationId/scenarios/create',
        to: '/next/negotiations/$negotiationId/scenarios/create',
    }),
    movedPage({ from: '/negotiations/create', to: '/next/negotiations/create' }),
    movedPage({ from: '/orders/upload', to: '/next/orders/upload' }),
    movedPage({ from: '/orders/{-$orderId}', to: '/next/orders/{-$orderId}' }),
    movedPage({ from: '/parts', to: '/next/parts' }),
    movedPage({ from: '/parts/alerts', to: '/next/alerts' }),
    movedPage({ from: '/parts/components', to: '/next/components' }),
    movedPage({ from: '/parts/components/ipn', to: '/next/components/ipn' }),
    movedPage({ from: '/parts/components/ipn/cpns', to: '/next/components/ipn/cpns' }),
    movedPage({ from: '/parts/components/ipn/linking', to: '/next/components/ipn/linking' }),
    movedPage({ from: '/parts/components/ipn/offers', to: '/next/components/ipn/offers' }),
    movedPage({
        from: '/parts/components/ipn/offers/$offerId/duplicate',
        to: '/next/components/ipn/offers/$offerId/duplicate',
    }),
    movedPage({ from: '/parts/components/ipn/offers/$offerId/edit', to: '/next/components/ipn/offers/$offerId/edit' }),
    movedPage({ from: '/parts/components/ipn/used-in', to: '/next/components/ipn/used-in' }),
    movedPage({ from: '/parts/components/linking', to: '/next/components/linking' }),
    movedPage({
        from: '/parts/custom/details/$customPartSpecificationId',
        to: '/next/parts/custom/$customPartSpecificationId',
    }),
    movedPage({
        from: '/parts/custom/details/$customPartSpecificationId/offers',
        to: '/next/parts/custom/$customPartSpecificationId/offers',
    }),
    movedPage({
        from: '/parts/custom/details/$customPartSpecificationId/used-in',
        to: '/next/parts/custom/$customPartSpecificationId/used-in',
    }),
    movedPage({ from: '/parts/demands', to: '/next/demands' }),
    movedPage({ from: '/parts/demands/importer', to: '/next/demands/importer' }),
    movedPage({ from: '/parts/details/$partId', to: '/next/parts/$partId' }),
    movedPage({ from: '/parts/details/$partId/compliance', to: '/next/parts/$partId/compliance' }),
    movedPage({ from: '/parts/details/$partId/offers', to: '/next/parts/$partId/offers' }),
    movedPage({ from: '/parts/details/$partId/part-alternatives', to: '/next/parts/$partId/part-alternatives' }),
    movedPage({ from: '/parts/details/$partId/se-alternatives', to: '/next/parts/$partId/se-alternatives' }),
    movedPage({ from: '/parts/details/$partId/se-documentation', to: '/next/parts/$partId/se-documentation' }),
    movedPage({ from: '/parts/details/$partId/se-geo-risk', to: '/next/parts/$partId/se-geo-risk' }),
    movedPage({ from: '/parts/details/$partId/se-supply-chain', to: '/next/parts/$partId/se-supply-chain' }),
    movedPage({ from: '/parts/details/$partId/se-technical', to: '/next/parts/$partId/se-technical' }),
    movedPage({ from: '/parts/details/$partId/used-in', to: '/next/parts/$partId/used-in' }),
    movedPage({ from: '/parts/offers/$offerId/duplicate', to: '/next/parts/offers/$offerId/duplicate' }),
    movedPage({ from: '/parts/offers/$offerId/edit', to: '/next/parts/offers/$offerId/edit' }),
    movedPage({ from: '/parts/quote-requests', to: '/next/quote-requests' }),
    movedPage({ from: '/parts/quote-requests/importer', to: '/next/quote-requests/importer' }),
    movedPage({ from: '/parts/search', to: '/next/parts/search' }),
    movedPage({ from: '/projects', to: '/next/projects' }),
    movedPage({ from: '/projects/$projectId', to: '/next/projects/$projectId' }),
    movedPage({ from: '/projects/$projectId/bom', to: '/next/projects/$projectId/assemblies' }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId',
        to: '/next/projects/$projectId/assemblies/$assemblyId',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/details',
        to: '/next/projects/$projectId/assemblies/$assemblyId/details',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/edit',
        to: '/next/projects/$projectId/assemblies/$assemblyId/edit',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/pcb',
        to: '/next/projects/$projectId/assemblies/$assemblyId/pcb',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/pcb/cad-viewer',
        to: '/next/projects/$projectId/assemblies/$assemblyId/pcb/cad-viewer',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/pcb/dfm',
        to: '/next/projects/$projectId/assemblies/$assemblyId/pcb/dfm',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/pcb/dfm-analysis',
        to: '/next/projects/$projectId/assemblies/$assemblyId/pcb/dfm-analysis',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/pcb/drills',
        to: '/next/projects/$projectId/assemblies/$assemblyId/pcb/drills',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/pcb/files',
        to: '/next/projects/$projectId/assemblies/$assemblyId/pcb/files',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/pcb/panelization',
        to: '/next/projects/$projectId/assemblies/$assemblyId/pcb/panelization',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/pcb/specification',
        to: '/next/projects/$projectId/assemblies/$assemblyId/pcb/specification',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/pcb/stackup',
        to: '/next/projects/$projectId/assemblies/$assemblyId/pcb/stackup',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/pnp-importer',
        to: '/next/projects/$projectId/assemblies/$assemblyId/pnp-importer',
    }),
    movedPage({
        from: '/projects/$projectId/bom/assembly/$assemblyId/subassemblies/create',
        to: '/next/projects/$projectId/assemblies/$assemblyId/subassemblies/create',
    }),
    movedPage({ from: '/projects/$projectId/dashboard', to: '/next/projects/$projectId/dashboard' }),
    movedPage({ from: '/projects/$projectId/edit', to: '/next/projects/$projectId/edit' }),
    movedPage({
        from: '/projects/$projectId/flexible-calculation',
        to: '/next/projects/$projectId/flexible-calculation',
    }),
    movedPage({
        from: '/projects/$projectId/flexible-calculation/$flexibleCalculationId',
        to: '/next/projects/$projectId/flexible-calculation/$flexibleCalculationId',
    }),
    movedPage({ from: '/projects/$projectId/manufacturing', to: '/next/projects/$projectId/manufacturing' }),
    movedPage({
        from: '/projects/$projectId/manufacturing/$manufacturingAssemblyDetailsId',
        to: '/next/projects/$projectId/manufacturing/$manufacturingAssemblyDetailsId',
    }),
    movedPage({
        from: '/projects/$projectId/manufacturing/$manufacturingAssemblyDetailsId/$manufacturingScenarioId/activity',
        to: '/next/projects/$projectId/manufacturing/$manufacturingAssemblyDetailsId/$manufacturingScenarioId/activity',
    }),
    movedPage({
        from: '/projects/$projectId/manufacturing/$manufacturingAssemblyDetailsId/$manufacturingScenarioId/expense',
        to: '/next/projects/$projectId/manufacturing/$manufacturingAssemblyDetailsId/$manufacturingScenarioId/expense',
    }),
    movedPage({ from: '/projects/$projectId/quotation', to: '/next/projects/$projectId/quotation' }),
    movedPage({ from: '/projects/$projectId/sourcing', to: '/next/projects/$projectId/sourcing' }),
    movedPage({
        from: '/projects/$projectId/sourcing/$sourcingScenarioId/line/$sourcingLineItemId',
        to: '/next/projects/$projectId/sourcing/scenarios/$sourcingScenarioId/line-items/$sourcingLineItemId',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/assembly/$assemblyId',
        to: '/next/projects/$projectId/sourcing/assembly/$assemblyId',
    }),
    movedPage({ from: '/projects/$projectId/sourcing/compare', to: '/next/projects/$projectId/sourcing/compare' }),
    movedPage({
        from: '/projects/$projectId/sourcing/offer/custom-part/$offerId/duplicate',
        to: '/next/projects/$projectId/sourcing/offers/custom-part/$offerId/duplicate',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/offer/custom-part/$offerId/edit',
        to: '/next/projects/$projectId/sourcing/offers/custom-part/$offerId/edit',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/offer/internal-part-number/$offerId/duplicate',
        to: '/next/projects/$projectId/sourcing/offers/internal-part-number/$offerId/duplicate',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/offer/internal-part-number/$offerId/edit',
        to: '/next/projects/$projectId/sourcing/offers/internal-part-number/$offerId/edit',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/offer/off-the-shelf/$offerId/duplicate',
        to: '/next/projects/$projectId/sourcing/offers/off-the-shelf/$offerId/duplicate',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/offer/off-the-shelf/$offerId/edit',
        to: '/next/projects/$projectId/sourcing/offers/off-the-shelf/$offerId/edit',
    }),
    movedPage({ from: '/projects/$projectId/sourcing/overview', to: '/next/projects/$projectId/sourcing/overview' }),
    movedPage({
        from: '/projects/$projectId/sourcing/overview/$view',
        to: '/next/projects/$projectId/sourcing/overview/$view',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/quote-requests',
        to: '/next/projects/$projectId/sourcing/quote-requests',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/quote-requests/$quoteRequestId',
        to: '/next/projects/$projectId/sourcing/quote-requests/$quoteRequestId',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/quote-requests/assign',
        to: '/next/projects/$projectId/sourcing/quote-requests/assign',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/quote-requests/attachments',
        to: '/next/projects/$projectId/sourcing/quote-requests/attachments',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/quote-requests/delivery-lot-size',
        to: '/next/projects/$projectId/sourcing/quote-requests/delivery-lot-size',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/quote-requests/importer',
        to: '/next/projects/$projectId/sourcing/quote-requests/importer',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/quote-requests/line-items',
        to: '/next/projects/$projectId/sourcing/quote-requests/line-items',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/quote-requests/preview-email',
        to: '/next/projects/$projectId/sourcing/quote-requests/preview-email',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/quote-requests/target-price',
        to: '/next/projects/$projectId/sourcing/quote-requests/target-price',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/scenarios/$sourcingScenarioId',
        to: '/next/projects/$projectId/sourcing/scenarios/$sourcingScenarioId',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/scenarios/$sourcingScenarioId/cost-breakdown',
        to: '/next/projects/$projectId/sourcing/scenarios/$sourcingScenarioId/cost-breakdown',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/scenarios/$sourcingScenarioId/cost-breakdown/assembly/$assemblyId/demand/$demandId',
        to: '/next/projects/$projectId/sourcing/scenarios/$sourcingScenarioId/cost-breakdown/assembly/$assemblyId/demand/$demandId',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/scenarios/$sourcingScenarioId/line-items',
        to: '/next/projects/$projectId/sourcing/scenarios/$sourcingScenarioId/line-items',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/scenarios/$sourcingScenarioId/purchase-orders',
        to: '/next/projects/$projectId/sourcing/scenarios/$sourcingScenarioId/purchase-orders',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/scenarios/create',
        to: '/next/projects/$projectId/sourcing/scenarios/create',
    }),
    movedPage({
        from: '/projects/$projectId/sourcing/version-history',
        to: '/next/projects/$projectId/sourcing/version-history',
    }),
    movedPage({ from: '/projects/create', to: '/next/projects/create' }),
    movedPage({ from: '/purchase-orders', to: '/next/purchase-orders' }),
    movedPage({ from: '/risk-management', to: '/next/risk-management' }),
    movedPage({ from: '/risk-management/geo-risk', to: '/next/risk-management/geo-risk' }),
    movedPage({ from: '/risk-management/library-health', to: '/next/risk-management/library-health' }),
    movedPage({
        from: '/risk-management/library-health/assemblies',
        to: '/next/risk-management/library-health/assemblies',
    }),
    movedPage({
        from: '/risk-management/library-health/components',
        to: '/next/risk-management/library-health/components',
    }),
    movedPage({ from: '/risk-management/library-health/parts', to: '/next/risk-management/library-health/parts' }),
    movedPage({ from: '/risk-management/pcns', to: '/next/risk-management/pcns' }),
    movedPage({ from: '/risk-management/risk-digest', to: '/next/risk-management/risk-digest' }),
    movedPage({ from: '/settings', to: '/next/settings' }),
    movedPage({ from: '/settings/company', to: '/next/settings/company' }),
    movedPage({ from: '/settings/organization', to: '/next/settings/organization' }),
    movedPage({ from: '/settings/organization/api-integration', to: '/next/settings/organization/api-integration' }),
    movedPage({
        from: '/settings/organization/assembly-settings',
        to: '/next/settings/organization/assembly-settings',
    }),
    movedPage({
        from: '/settings/organization/automatic-rfq-number',
        to: '/next/settings/organization/automatic-rfq-number',
    }),
    movedPage({ from: '/settings/organization/branding', to: '/next/settings/organization/branding' }),
    movedPage({
        from: '/settings/organization/carbon-footprints',
        to: '/next/settings/organization/carbon-footprints',
    }),
    movedPage({
        from: '/settings/organization/component-settings',
        to: '/next/settings/organization/component-settings',
    }),
    movedPage({ from: '/settings/organization/csv-integration', to: '/next/settings/organization/csv-integration' }),
    movedPage({
        from: '/settings/organization/custom-part-categories',
        to: '/next/settings/organization/custom-part-categories',
    }),
    movedPage({ from: '/settings/organization/customer-portal', to: '/next/settings/organization/customer-portal' }),
    movedPage({
        from: '/settings/organization/customer-portal/additional-services',
        to: '/next/settings/organization/customer-portal/additional-services',
    }),
    movedPage({
        from: '/settings/organization/customer-portal/checkout-conditions',
        to: '/next/settings/organization/customer-portal/checkout-conditions',
    }),
    movedPage({
        from: '/settings/organization/customer-portal/customer-specific-settings',
        to: '/next/settings/organization/customer-portal/customer-specific-settings',
    }),
    movedPage({
        from: '/settings/organization/customer-portal/landing-page',
        to: '/next/settings/organization/customer-portal/landing-page',
    }),
    movedPage({
        from: '/settings/organization/customer-portal/payment-credentials',
        to: '/next/settings/organization/customer-portal/payment-credentials',
    }),
    movedPage({
        from: '/settings/organization/data-integrations',
        to: '/next/settings/organization/data-integrations',
    }),
    movedPage({ from: '/settings/organization/feature-flags', to: '/next/settings/organization/feature-flags' }),
    movedPage({
        from: '/settings/organization/manufacturer-preferences',
        to: '/next/settings/organization/manufacturer-preferences',
    }),
    movedPage({ from: '/settings/organization/pcb', to: '/next/settings/organization/pcb' }),
    movedPage({
        from: '/settings/organization/pcb/panel-preferences',
        to: '/next/settings/organization/pcb/panel-preferences',
    }),
    movedPage({
        from: '/settings/organization/pcb/panel-preferences/$panelPreferenceId/edit',
        to: '/next/settings/organization/pcb/panel-preferences/$panelPreferenceId/edit',
    }),
    movedPage({
        from: '/settings/organization/pcb/panel-preferences/create',
        to: '/next/settings/organization/pcb/panel-preferences/create',
    }),
    movedPage({ from: '/settings/organization/sites', to: '/next/settings/organization/sites' }),
    movedPage({
        from: '/settings/organization/supplier-api-integrations',
        to: '/next/settings/organization/supplier-api-integrations',
    }),
    movedPage({
        from: '/settings/organization/user-management/action-permissions',
        to: '/next/settings/organization/user-management/action-permissions',
    }),
    movedPage({
        from: '/settings/organization/user-management/roles',
        to: '/next/settings/organization/user-management/roles',
    }),
    movedPage({ from: '/settings/organization/users', to: '/next/settings/organization/users' }),
    movedPage({
        from: '/settings/organization/users/importer',
        to: '/next/settings/organization/users/importer',
    }),
    movedPage({ from: '/settings/user/account-security', to: '/next/settings/user/account-security' }),
    movedPage({ from: '/suppliers', to: '/next/suppliers' }),
    movedPage({ from: '/suppliers/$supplierAndStockLocationId', to: '/next/suppliers/$supplierAndStockLocationId' }),
    movedPage({ from: '/suppliers/strategy', to: '/next/suppliers/strategy' }),
    movedPage({ from: '/suppliers/supplier-contacts', to: '/next/suppliers/supplier-contacts' }),
    movedPage({ from: '/templates/email', to: '/next/templates/email' }),
    movedPage({ from: '/templates/email/$emailTemplateId/edit', to: '/next/templates/email/$emailTemplateId/edit' }),
    movedPage({ from: '/templates/email/create', to: '/next/templates/email/create' }),
    movedPage({ from: '/templates/export', to: '/next/templates/export' }),
    movedPage({ from: '/templates/flexible-calculation', to: '/next/templates/flexible-calculation' }),
    movedPage({
        from: '/templates/flexible-calculation/$flexibleCalculationTemplateId',
        to: '/next/templates/flexible-calculation/$flexibleCalculationTemplateId',
    }),
    movedPage({
        from: '/templates/flexible-calculation/apps/$appId/$widgetId',
        to: '/next/templates/flexible-calculation/apps/$appId/$widgetId',
    }),
    movedPage({ from: '/templates/flexible-calculation/create', to: '/next/templates/flexible-calculation/create' }),
    movedPage({
        from: '/templates/flexible-calculation/exported-variables',
        to: '/next/templates/flexible-calculation/exported-variables',
    }),
    movedPage({
        from: '/templates/flexible-calculation/review-conversion/$legacyTemplateId/$flexibleCalculationTemplateId',
        to: '/next/templates/flexible-calculation/review-conversion/$legacyTemplateId/$flexibleCalculationTemplateId',
    }),
    movedPage({ from: '/templates/pcb-specification', to: '/next/templates/pcb-specification' }),
    movedPage({ from: '/templates/quotation', to: '/next/templates/quotation' }),
    movedPage({
        from: '/templates/quotation/builder/{-$templateId}',
        to: '/next/templates/quotation/builder/{-$templateId}',
    }),
    movedPage({ from: '/templates/quote-request', to: '/next/templates/quote-request' }),
    movedPage({
        from: '/templates/quote-request/$quoteRequestTemplateId/edit',
        to: '/next/templates/quote-request/$quoteRequestTemplateId/edit',
    }),
    movedPage({ from: '/templates/quote-request/create', to: '/next/templates/quote-request/create' }),
    movedPage({ from: '/templates/sourcing', to: '/next/templates/sourcing' }),
    movedPage({
        from: '/templates/sourcing/$sourcingTemplateId/duplicate',
        to: '/next/templates/sourcing/$sourcingTemplateId/duplicate',
    }),
    movedPage({
        from: '/templates/sourcing/$sourcingTemplateId/edit',
        to: '/next/templates/sourcing/$sourcingTemplateId/edit',
    }),
    movedPage({ from: '/templates/sourcing/create', to: '/next/templates/sourcing/create' }),
];
